import Highcharts, { SeriesFlagsOptions } from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useState, useEffect, useRef } from "react";
import annotations from "highcharts/modules/annotations";
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import noDataToDisplay from "highcharts/modules/no-data-to-display";
import accessibility from "highcharts/modules/accessibility";
import { Box } from "@mui/material";
import "./TimeSeriesChart.css";
import { UserRole } from "models/IUser";
import { getUserRoleName } from "helpers/UserHelper";
import { useTranslation } from "react-i18next";
import { getAnxietyLevel, getClimateChangeAnxietyLevel, getDepressionLevel, getWellBeingLevel } from "helpers/SMILEHelper";

annotations(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
noDataToDisplay(Highcharts);
accessibility(Highcharts);

interface CustomPointOptionsObject extends Highcharts.PointOptionsObject {
  units?: string;
}

export type TimeSeriesValue = {
  id: string;
  name: string;
  date: Date;
  value: number;
  units: string | undefined;
};

export type TimeSeriesAnnotation = {
  userRole: UserRole;
  date: Date;
  description: string;
};

type TimeSeriesChartProps = {
  showLoading: boolean;
  values: TimeSeriesValue[] | undefined;
  annotations: TimeSeriesAnnotation[] | undefined;
  handleOpenAddAnnotationDialogWithTimelineDate: (timelineDate: Date) => void;
  enableAuraSplitAxes?: boolean;
};

const SECONDARY_SERIES_NAMES = new Set(["Distance pulse", "Distance heart sound"]);
const TOP_RIGHT_SERIES_NAMES = new Set(["BR"]);
const BOTTOM_RIGHT_SERIES_NAMES = new Set(["HR"]);

const getPaneIndexForSeries = (seriesName: string): number => {
  if (SECONDARY_SERIES_NAMES.has(seriesName) || BOTTOM_RIGHT_SERIES_NAMES.has(seriesName)) {
    return 1;
  }

  return 0;
};

const getAxisIndexForSeries = (seriesName: string): number => {
  if (TOP_RIGHT_SERIES_NAMES.has(seriesName)) {
    return 1;
  }

  if (SECONDARY_SERIES_NAMES.has(seriesName)) {
    return 2;
  }

  if (BOTTOM_RIGHT_SERIES_NAMES.has(seriesName)) {
    return 3;
  }

  return 0;
};

const groupBy = <K, V>(list: Array<V>, keyGetter: (input: V) => K) => {
  const map = new Map<K, Array<V>>();
  list.forEach((item: any) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
};

const TimeSeriesChart = ({
  showLoading,
  values,
  annotations,
  handleOpenAddAnnotationDialogWithTimelineDate,
  enableAuraSplitAxes = false,
}: TimeSeriesChartProps) => {
  const { t } = useTranslation();
  const xAxisOptions: Highcharts.XAxisOptions | Highcharts.XAxisOptions[] = enableAuraSplitAxes
    ? [
      {
        type: "datetime",
        height: "42%",
        top: "0%",
        title: {
          text: t('date', 'Date'),
        },
      },
      {
        type: "datetime",
        linkedTo: 0,
        height: "42%",
        top: "52%",
        offset: 0,
        title: {
          text: t('date', 'Date'),
        },
      },
    ]
    : {
      type: "datetime",
      title: {
        text: t('date', 'Date'),
      },
    };

  const yAxisOptions: Highcharts.YAxisOptions | Highcharts.YAxisOptions[] = enableAuraSplitAxes
    ? [
      {
        height: "42%",
        top: "0%",
        lineWidth: 1,
        opposite: false,
        resize: {
          enabled: true,
        },
        title: {
          text: t('values', 'Values') + " (Distance / Distance breath)",
        },
        allowDecimals: true,
      },
      {
        height: "42%",
        top: "0%",
        offset: 0,
        lineWidth: 1,
        opposite: true,
        title: {
          text: t('values', 'Values') + " (BR)",
        },
        allowDecimals: true,
      },
      {
        height: "42%",
        top: "52%",
        offset: 0,
        lineWidth: 1,
        opposite: false,
        title: {
          text: t('values', 'Values') + " (Distance pulse / Distance heart sound)",
        },
        allowDecimals: true,
      },
      {
        height: "42%",
        top: "52%",
        offset: 0,
        lineWidth: 1,
        opposite: true,
        title: {
          text: t('values', 'Values') + " (HR)",
        },
        allowDecimals: true,
      },
    ]
    : {
      title: {
        text: t('values', 'Values'),
      },
      opposite: false,
      allowDecimals: true,
    };
  
  const HighchartsOptions: Highcharts.Options = {
    mapNavigation: {
      enableMouseWheelZoom: true,
      mouseWheelSensitivity: 1.1,
    },
    chart: {
      zooming: {
        key: "alt",
        pinchType: "x",
        singleTouch: false,
        type: "x",
      },
      panning: {
        enabled: true,
        type: "x",
      },
      panKey: "shift",
    },
    rangeSelector: {
      enabled: false,
    },
    plotOptions: {
      series: {
        showInNavigator: true,
        states: {
          inactive: {
            enabled: false,
          },
        },
        point: {
          events: {
            click: (e) => {
              const date = new Date((e.point as any).x!!)
              handleOpenAddAnnotationDialogWithTimelineDate(date)
            }
          }
        }
      },
      flags: {
        tooltip: {
          headerFormat: "<b>" + t('date', 'Date') + ":</b> {point.x:%m-%d-%Y %H:%M:%S}<br><br>",
          pointFormatter: function (this: Highcharts.Point) {
            return (this as any).text;
          }
        }
      }
    },
    xAxis: xAxisOptions,
    yAxis: yAxisOptions,
    lang: {
      noData: t('no_data_to_display', 'There is no data to display.')!!,
    },
    noData: {
      style: {
        fontWeight: "bold",
        fontSize: "15px",
        color: "#303030",
      },
    },
    tooltip: {
      headerFormat: "<b>" + t('date', 'Date') + ":</b> {point.x:%d/%m/%Y %H:%M:%S}<br>",
      pointFormatter: function () {
        const point = this as unknown as CustomPointOptionsObject;
        if (this.series.name.includes('Anxiety level')) {
          return '<span style="color:' + this.series.color + '">' + this.series.name + '</span>: ' + getAnxietyLevel(point.y!!) + ' ' + point.units + '<br>';
        }
        else if (this.series.name.includes('Depression level')) {
          return '<span style="color:' + this.series.color + '">' + this.series.name + '</span>: ' + getDepressionLevel(point.y!!) + ' ' + point.units + '<br>';
        }
        else if (this.series.name.includes('Well-being level')) {
          return '<span style="color:' + this.series.color + '">' + this.series.name + '</span>: ' + getWellBeingLevel(point.y!!) + ' ' + point.units + '<br>';
        }
        else if (this.series.name.includes('Climate change anxiety level')) {
          return '<span style="color:' + this.series.color + '">' + this.series.name + '</span>: ' + getClimateChangeAnxietyLevel(point.y!!) + ' ' + point.units + '<br>';
        }
        else if (this.series.name.includes('Resilience level')) {
          return '<span style="color:' + this.series.color + '">' + this.series.name + '</span>: ' + getResilienceLevel(point.y!!) + ' ' + point.units + '<br>';
        }
        else {
          return '<span style="color:' + this.series.color + '">' + this.series.name + '</span>: ' + point.y?.toFixed(2) + ' ' + point.units + '<br>';
        }
      },
      split: false,
      shared: true,
    },
    legend: {
      enabled: true,
    },
    credits: {
      enabled: false,
    },
    time: {
      //timezoneOffset: new Date().getTimezoneOffset()
      useUTC: false,
    }
  };

  const chartComponentRef = useRef<any>(null);
  const [highchartsOption, setHighchartsOption] = useState<Highcharts.Options>(HighchartsOptions);

  useEffect(() => {
    if (values && values.filter((c) => c.date).length > 0) {
      const seriesOptions: Highcharts.SeriesOptionsType[] = [];
      
      if (annotations && annotations.length > 0) {
        const flags = annotations.map((annotation) => {
          return {
            x: new Date(annotation.date).getTime(),
            text: annotation.description,
            title: Array.from(getUserRoleName(annotation.userRole))[0],
          } as Highcharts.PointOptionsObject;
        });
        const flagSeriesOptions: SeriesFlagsOptions = {
          type: "flags",
          name: "Highcharts",
          shape: "circlepin",
          data: flags,
          showInLegend: false,
          color: "#333333"
        };
        seriesOptions.push(flagSeriesOptions);
      }

      const groupedMap = groupBy<string, TimeSeriesValue>(
        values,
        (value: TimeSeriesValue) => value.name
      );

      for (let [k, v] of groupedMap) {
        const data = v.map((c) => [c.date.getTime(), c.value, c.units]);
        const paneIndex = enableAuraSplitAxes ? getPaneIndexForSeries(k) : 0;
        const axisIndex = enableAuraSplitAxes ? getAxisIndexForSeries(k) : 0;
        const seriesOption: Highcharts.SeriesOptionsType = {
          type: "line",
          name: k,
          data: data,
          showInLegend: true,
          keys: ["x", "y", "units"],
          xAxis: paneIndex,
          yAxis: axisIndex,
        };
        seriesOptions.push(seriesOption);
      }

      setHighchartsOption({ ...HighchartsOptions, series: seriesOptions });
      chartComponentRef.current?.chart.redraw();
    } else {
      if (chartComponentRef.current?.chart.series.length === undefined || chartComponentRef.current?.chart.series.length > 0) {
        setHighchartsOption({ ...HighchartsOptions, series: [] });
        chartComponentRef.current?.chart.redraw();
        chartComponentRef.current?.chart.xAxis[0].setExtremes()
      }
    }
  }, [values, annotations, t, enableAuraSplitAxes]);

  useEffect(() => {
    if (chartComponentRef?.current) {
      const chart = chartComponentRef.current.chart;
      if (showLoading) {
        chart.showLoading("");
        (chart as any).hideNoData();
      } else {
        chart.hideLoading();
        if (!chart.series || chart.series.length === 0 || chart.series.every((series: Highcharts.Series) => !series.visible || !series.data || series.data.length === 0)) {
          (chart as any).showNoData();
        }
      }
      chart.reflow();
    }
  }, [showLoading, t]);

  return (
    <Box>
      <HighchartsReact
        ref={chartComponentRef}
        highcharts={Highcharts}
        constructorType={"stockChart"}
        options={highchartsOption}
      />
    </Box>
  );
};

export default TimeSeriesChart;
function getResilienceLevel(arg0: number) {
  throw new Error("Function not implemented.");
}

